import React, { useState, useEffect } from 'react'
import { backendService, DoctorSummary, DoctorAiTokenBalance, DoctorAiTokenTransaction } from '../../services/backendService'
import { logInfo, logError } from '../../utils/errorLogger'
import './BalanceManagement.css'

export const DoctorAiTokenManagement: React.FC = () => {
  const [doctors, setDoctors] = useState<DoctorSummary[]>([])
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorSummary | null>(null)
  const [aiTokenData, setAiTokenData] = useState<DoctorAiTokenBalance | null>(null)

  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false)
  const [isLoadingBalance, setIsLoadingBalance] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [searchTerm, setSearchTerm] = useState('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [isCredit, setIsCredit] = useState(true)

  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Load doctors on mount
  useEffect(() => {
    loadDoctors()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load AI token balance when doctor is selected
  useEffect(() => {
    if (selectedDoctor) {
      loadAiTokenBalance()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctor])

  const loadDoctors = async () => {
    setIsLoadingDoctors(true)
    try {
      const response = await backendService.listAllDoctors(1, 100, searchTerm, 'name', 'asc', true)
      setDoctors(response.doctors)
      logInfo(`Loaded ${response.doctors.length} doctors`, 'DoctorAiTokenManagement')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load doctors'
      logError(`Failed to load doctors: ${errorMessage}`, 'DoctorAiTokenManagement')
    } finally {
      setIsLoadingDoctors(false)
    }
  }

  const loadAiTokenBalance = async () => {
    if (!selectedDoctor) return

    setIsLoadingBalance(true)
    try {
      const data = await backendService.getDoctorAiTokenBalance(selectedDoctor.id)
      setAiTokenData(data)
      logInfo(`Loaded AI token balance for ${selectedDoctor.name}: ${data.balance} tokens`, 'DoctorAiTokenManagement')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load AI token balance'
      logError(`Failed to load AI token balance: ${errorMessage}`, 'DoctorAiTokenManagement')
    } finally {
      setIsLoadingBalance(false)
    }
  }

  const handleDoctorSelect = (doctor: DoctorSummary) => {
    setSelectedDoctor(doctor)
    setAiTokenData(null)
    setAmount('')
    setReason('')
    setSubmitSuccess(false)
    setSubmitError(null)
    logInfo(`Selected doctor: ${doctor.name} (${doctor.username})`, 'DoctorAiTokenManagement')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedDoctor) {
      setSubmitError('Please select a doctor first')
      return
    }

    const amountNum = parseInt(amount)
    if (isNaN(amountNum) || amountNum <= 0) {
      setSubmitError('Please enter a valid positive amount')
      return
    }

    if (!reason.trim()) {
      setSubmitError('Please enter a reason')
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      const result = isCredit
        ? await backendService.creditDoctorAiTokens(selectedDoctor.id, amountNum, reason.trim())
        : await backendService.debitDoctorAiTokens(selectedDoctor.id, amountNum, reason.trim())

      logInfo(`AI tokens ${isCredit ? 'credited' : 'debited'} successfully. New balance: ${result.balance}`, 'DoctorAiTokenManagement')

      setAiTokenData(result)
      setSubmitSuccess(true)
      setAmount('')
      setReason('')

      setTimeout(() => {
        setSubmitSuccess(false)
      }, 3000)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update AI tokens'
      logError(`Failed to update AI tokens: ${errorMessage}`, 'DoctorAiTokenManagement')
      setSubmitError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const filteredDoctors = doctors.filter(doctor =>
    doctor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doctor.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doctor.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const transactions = aiTokenData?.transactions || []

  return (
    <div className="balance-management">
      <div className="balance-layout">
        {/* Left Panel: Doctor Selection */}
        <div className="doctor-selector-panel">
          <h3 className="panel-title">Select Doctor</h3>

          <div className="search-box">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, username, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isLoadingDoctors ? (
            <div className="loading-state">
              <span className="spinner"></span>
              <span>Loading doctors...</span>
            </div>
          ) : (
            <div className="doctor-list">
              {filteredDoctors.length === 0 ? (
                <div className="empty-state">
                  <p>No doctors found</p>
                </div>
              ) : (
                filteredDoctors.map(doctor => (
                  <div
                    key={doctor.id}
                    className={`doctor-item ${selectedDoctor?.id === doctor.id ? 'selected' : ''}`}
                    onClick={() => handleDoctorSelect(doctor)}
                  >
                    <div className="doctor-info">
                      <div className="doctor-name">{doctor.name}</div>
                      <div className="doctor-username">@{doctor.username}</div>
                    </div>
                    <div className="doctor-balance">
                      <span className="balance-amount">{doctor.aiTokenBalance}</span>
                      <span className="balance-label">AI tokens</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Right Panel: AI Token Management */}
        <div className="balance-details-panel">
          {!selectedDoctor ? (
            <div className="empty-state-large">
              <div className="empty-icon">👈</div>
              <h3>Select a Doctor</h3>
              <p>Choose a doctor from the list to manage their AI token balance</p>
            </div>
          ) : (
            <>
              {/* Success/Error Messages */}
              {submitSuccess && (
                <div className="alert alert-success">
                  <span className="alert-icon">✅</span>
                  <div className="alert-content">
                    <strong>Success!</strong> AI tokens updated successfully.
                  </div>
                </div>
              )}

              {submitError && (
                <div className="alert alert-error">
                  <span className="alert-icon">❌</span>
                  <div className="alert-content">
                    <strong>Error:</strong> {submitError}
                  </div>
                </div>
              )}

              {/* Current AI Token Balance Card */}
              <div className="balance-card">
                <h3 className="card-title">User AI Token Balance</h3>
                <div className="doctor-header">
                  <div className="doctor-name-large">{selectedDoctor.name}</div>
                  <div className="doctor-meta">
                    @{selectedDoctor.username} &bull; {selectedDoctor.email}
                  </div>
                </div>

                {isLoadingBalance ? (
                  <div className="loading-state">
                    <span className="spinner"></span>
                    <span>Loading AI token balance...</span>
                  </div>
                ) : aiTokenData ? (
                  <div className="balance-stats">
                    <div className="stat-item stat-primary">
                      <div className="stat-label">AI Token Balance</div>
                      <div className="stat-value">{aiTokenData.balance}</div>
                      <div className="stat-unit">AI tokens</div>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Credit/Debit AI Tokens Form */}
              <div className="balance-form-card">
                <h3 className="card-title">Manage User AI Tokens</h3>
                <form onSubmit={handleSubmit} className="balance-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="aiTransactionType" className="form-label">
                        Transaction Type
                      </label>
                      <select
                        id="aiTransactionType"
                        className="form-input"
                        value={isCredit ? 'credit' : 'debit'}
                        onChange={(e) => setIsCredit(e.target.value === 'credit')}
                        disabled={isSubmitting}
                      >
                        <option value="credit">Add AI Tokens (Credit)</option>
                        <option value="debit">Deduct AI Tokens (Debit)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="aiAmount" className="form-label">
                        Amount <span className="required">*</span>
                      </label>
                      <input
                        id="aiAmount"
                        type="number"
                        className="form-input"
                        placeholder="100"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        disabled={isSubmitting}
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="aiReason" className="form-label">
                      Reason <span className="required">*</span>
                    </label>
                    <input
                      id="aiReason"
                      type="text"
                      className="form-input"
                      placeholder="Monthly AI token allocation"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Processing...
                      </>
                    ) : (
                      isCredit ? 'Add AI Tokens' : 'Deduct AI Tokens'
                    )}
                  </button>
                </form>
              </div>

              {/* AI Token Transaction History */}
              <div className="transaction-history-card">
                <h3 className="card-title">AI Token Transaction History</h3>

                {isLoadingBalance ? (
                  <div className="loading-state">
                    <span className="spinner"></span>
                    <span>Loading transactions...</span>
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="empty-state">
                    <p>No AI token transactions yet</p>
                  </div>
                ) : (
                  <div className="transaction-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Reason</th>
                          <th>By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx: DoctorAiTokenTransaction) => (
                          <tr key={tx.id}>
                            <td className="date-cell">
                              {new Date(tx.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td>
                              <span className={`transaction-type ${tx.amount >= 0 ? 'credit' : 'debit'}`}>
                                {tx.amount >= 0 ? '+ Credit' : '- Debit'}
                              </span>
                            </td>
                            <td className={`amount-cell ${tx.amount >= 0 ? 'credit' : 'debit'}`}>
                              {tx.amount >= 0 ? '+' : ''}{tx.amount}
                            </td>
                            <td className="description-cell">{tx.reason}</td>
                            <td className="description-cell">{tx.created_by || 'admin'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
